"use client";

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { useSelectedLayoutSegment } from "next/navigation";
import { signOut } from "next-auth/react";
import { useTheme } from "next-themes";
import { useStoreState } from "easy-peasy";
import { AnimatePresence, motion } from "motion/react";
import {
  ChevronRight,
  LogIn,
  LogOut,
  Package,
  Plus,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  BookOpen,
  Sun,
  Moon,
  Wallet,
  X,
  User as UserIcon,
} from "lucide-react";
import { toast } from "sonner";

import { siteConfig } from "@/config/site";
import { cn } from "@/lib/utils";
import { Icons } from "@/components/shared/icons";
import { UserAvatar } from "@/components/shared/user-avatar";
import { getUserBalance } from "@/actions/reviews";
import type { StoreModel } from "@/store";

/* ------------------------------------------------------------------ */
/*  Types & Interfaces                                                */
/* ------------------------------------------------------------------ */

export interface MobileDrawerUser {
  name?: string | null;
  email?: string | null;
  image?: string | null;
  role?: string;
}

export interface MobileNavItem {
  label: string;
  link: string;
  icon?: React.ComponentType<{ className?: string }>;
  description?: string;
  badge?: string;
  badgeVariant?: "default" | "admin";
  external?: boolean;
  isActive?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Shared Component: DrawerThemeSwitcher                             */
/* ------------------------------------------------------------------ */

export function DrawerThemeSwitcher() {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <div className="h-9 w-full rounded-xl bg-muted/40 animate-pulse border border-border/50" />
    );
  }

  return (
    <div className="flex items-center rounded-xl bg-card/60 p-1 border border-border/60 shadow-2xs">
      <button
        type="button"
        onClick={() => setTheme("light")}
        className={cn(
          "flex flex-1 items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-medium transition-all",
          theme === "light"
            ? "bg-primary/10 text-primary border border-primary/20 shadow-2xs font-semibold"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
        )}
        aria-label="Light mode"
      >
        <Sun className="size-3.5" />
        <span>Light</span>
      </button>

      <button
        type="button"
        onClick={() => setTheme("dark")}
        className={cn(
          "flex flex-1 items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-medium transition-all",
          theme === "dark"
            ? "bg-primary/10 text-primary border border-primary/20 shadow-2xs font-semibold"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/40"
        )}
        aria-label="Dark mode"
      >
        <Moon className="size-3.5" />
        <span>Dark</span>
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared Component: DrawerBalanceCard                               */
/* ------------------------------------------------------------------ */

export function DrawerBalanceCard({ onAddFunds }: { onAddFunds?: () => void }) {
  const [balance, setBalance] = useState<number>(0);

  const fetchBalance = () => {
    getUserBalance()
      .then((val) => setBalance(val))
      .catch(() => { });
  };

  useEffect(() => {
    fetchBalance();
    const handleUpdate = () => fetchBalance();
    window.addEventListener("balance-updated", handleUpdate);
    return () => window.removeEventListener("balance-updated", handleUpdate);
  }, []);

  const handleDepositClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAddFunds) {
      onAddFunds();
    } else {
      toast.info("Instant balance deposits will be available with crypto & card gateway.", {
        description: `Your current available balance is $${balance.toFixed(2)}.`,
      });
    }
  };

  return (
    <div className="flex items-center justify-between rounded-xl border border-border/60 bg-card/60 p-3 shadow-2xs backdrop-blur-md">
      <div className="flex items-center gap-2.5">
        <div className="flex size-8 items-center justify-center rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
          <Wallet className="size-4" />
        </div>
        <div className="flex flex-col text-left leading-none">
          <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            Available Balance
          </span>
          <span className="mt-1 font-mono text-sm font-bold text-foreground">
            ${balance.toFixed(2)}
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={handleDepositClick}
        className="flex items-center gap-1 rounded-lg border border-border/60 bg-muted/60 px-2 py-1 text-[11px] font-medium text-foreground hover:bg-primary/10 hover:border-primary/30 hover:text-primary transition-all active:scale-95"
      >
        <Plus className="size-3 stroke-[2.5]" />
        <span>Deposit</span>
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared Component: DrawerCartCard                                  */
/* ------------------------------------------------------------------ */

export function DrawerCartCard({ onClose }: { onClose: () => void }) {
  const items = useStoreState((state: StoreModel) => state.cart.items);
  const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Link
      href="/checkout"
      onClick={onClose}
      className="flex items-center justify-between rounded-xl border border-border/60 bg-card/60 p-3 shadow-2xs backdrop-blur-md hover:border-primary/30 hover:bg-muted/40 transition-all group"
    >
      <div className="flex items-center gap-2.5">
        <div className="relative flex size-8 items-center justify-center rounded-lg bg-violet-500/10 border border-violet-500/20 text-violet-400 group-hover:scale-105 transition-transform">
          <ShoppingCart className="size-4" />
          {totalQty > 0 && (
            <span className="absolute -top-1 -right-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-violet-600 px-1 text-[9px] font-bold leading-none text-white shadow">
              {totalQty > 99 ? "99+" : totalQty}
            </span>
          )}
        </div>
        <div className="flex flex-col text-left leading-none">
          <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            Shopping Cart
          </span>
          <span className="mt-1 text-sm font-semibold text-foreground">
            {totalQty === 0
              ? "Empty cart"
              : `${totalQty} item${totalQty === 1 ? "" : "s"}`}
          </span>
        </div>
      </div>

      <ChevronRight className="size-4 text-muted-foreground/60 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/*  Left Navigation Drawer (Steam-style Left Hamburger Drawer)        */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  Left Navigation Drawer (Dedicated Site Navigation)                */
/* ------------------------------------------------------------------ */

export interface LeftNavDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  items: MobileNavItem[];
}

export function LeftNavDrawer({
  isOpen,
  onClose,
  items,
}: LeftNavDrawerProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!mounted) return null;

  const content = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop blur */}
          <motion.div
            key="left-drawer-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            onClick={onClose}
            className="absolute inset-0 bg-black/75 backdrop-blur-md"
            aria-hidden="true"
          />

          {/* Drawer Panel */}
          <motion.aside
            key="left-drawer-panel"
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
            className="absolute inset-y-0 left-0 flex size-full max-w-[325px] flex-col bg-background/95 p-5 shadow-2xl backdrop-blur-2xl text-foreground border-r border-border/60"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation Menu"
          >
            {/* Header */}
            <div className="flex items-center justify-between pb-4 border-b border-border/50">
              <Link
                href="/"
                onClick={onClose}
                className="flex items-center space-x-2.5 outline-none"
              >
                <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10 border border-primary/20 text-primary">
                  <Icons.logo className="size-5" />
                </div>
                <div className="flex flex-col">
                  <span className="font-urban text-base font-bold tracking-tight text-foreground">
                    {siteConfig.name}
                  </span>
                  <span className="text-[10px] font-mono font-medium tracking-wider text-muted-foreground uppercase">
                    DMA Software
                  </span>
                </div>
              </Link>

              <button
                type="button"
                onClick={onClose}
                className="flex size-8 items-center justify-center rounded-full border border-border/60 bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label="Close menu"
              >
                <X className="size-4" strokeWidth={2.2} />
              </button>
            </div>

            {/* Scrollable Container */}
            <div className="flex-1 overflow-y-auto py-4 space-y-4 justify-between flex flex-col">
              <div className="space-y-4">
                {/* Theme Selector */}
                <DrawerThemeSwitcher />

                {/* Navigation Items */}
                <div className="space-y-1 pt-1">
                  <p className="px-1 text-[10px] font-mono font-semibold uppercase tracking-wider text-muted-foreground">
                    Navigation
                  </p>
                  <ul className="flex flex-col gap-2 p-0 m-0 list-none" role="list">
                    {items.map((item, idx) => {
                      const IconComponent = item.icon;
                      return (
                        <li key={item.label + idx}>
                          <Link
                            href={item.link}
                            onClick={onClose}
                            className={cn(
                              "flex items-center justify-between rounded-xl p-2.5 border transition-all duration-150 group cursor-pointer",
                              item.isActive
                                ? "bg-primary/10 border-primary/30 text-primary shadow-2xs"
                                : "bg-card/40 border-border/40 hover:bg-muted/70 hover:border-border text-foreground"
                            )}
                          >
                            <div className="flex items-center gap-3">
                              {IconComponent && (
                                <div
                                  className={cn(
                                    "flex size-8 items-center justify-center rounded-lg border transition-transform group-hover:scale-105 shrink-0",
                                    item.isActive
                                      ? "bg-primary/20 border-primary/40 text-primary"
                                      : "bg-muted/80 border-border/50 text-muted-foreground group-hover:text-primary group-hover:bg-primary/10"
                                  )}
                                >
                                  <IconComponent className="size-4" />
                                </div>
                              )}
                              <div className="flex flex-col">
                                <div className="flex items-center gap-2">
                                  <span
                                    className={cn(
                                      "font-heading text-sm font-semibold tracking-tight transition-colors",
                                      item.isActive
                                        ? "text-primary"
                                        : "text-foreground group-hover:text-primary"
                                    )}
                                  >
                                    {item.label}
                                  </span>
                                </div>
                                {item.description && (
                                  <span className="text-[11px] text-muted-foreground line-clamp-1">
                                    {item.description}
                                  </span>
                                )}
                              </div>
                            </div>

                            <ChevronRight
                              className={cn(
                                "size-4 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5 shrink-0",
                                item.isActive && "text-primary"
                              )}
                            />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="pt-3 border-t border-border/50 flex flex-col gap-2.5 mt-auto">
              {siteConfig.links.discord && (
                <a
                  href={siteConfig.links.discord}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 rounded-xl border border-[#5865F2]/30 bg-[#5865F2]/10 py-2.5 px-3 text-xs font-semibold text-[#5865F2] hover:bg-[#5865F2] hover:text-white transition-all shadow-2xs group"
                >
                  <Icons.discord className="size-4 transition-transform group-hover:scale-110" />
                  <span>Join Discord Community</span>
                </a>
              )}

              <div className="flex items-center justify-between text-[10px] font-mono text-muted-foreground/80 pt-1">
                <div className="flex items-center gap-1.5">
                  <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span>Systems Undetected</span>
                </div>
                <span>v2.4</span>
              </div>
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );

  return typeof document !== "undefined" && document.body
    ? createPortal(content, document.body)
    : null;
}

/* ------------------------------------------------------------------ */
/*  Right Account Drawer (Dedicated Account & Commerce Hub)           */
/* ------------------------------------------------------------------ */

export interface RightAccountDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  user: MobileDrawerUser;
  onSignOut?: () => void;
}

export function RightAccountDrawer({
  isOpen,
  onClose,
  user,
  onSignOut,
}: RightAccountDrawerProps) {
  const [mounted, setMounted] = useState(false);
  const segment = useSelectedLayoutSegment();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!mounted) return null;

  const content = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop blur */}
          <motion.div
            key="account-drawer-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            onClick={onClose}
            className="absolute inset-0 bg-black/75 backdrop-blur-md"
            aria-hidden="true"
          />

          {/* Drawer Panel */}
          <motion.aside
            key="account-drawer-panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
            className="absolute inset-y-0 right-0 flex size-full max-w-[325px] flex-col bg-background/95 p-5 shadow-2xl backdrop-blur-2xl text-foreground border-l border-border/60"
            role="dialog"
            aria-modal="true"
            aria-label="Account Menu"
          >
            {/* Header */}
            <div className="flex items-center justify-between pb-4 border-b border-border/50">
              <div className="flex items-center space-x-2.5">
                <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10 border border-primary/20 text-primary">
                  <UserIcon className="size-5" />
                </div>
                <div className="flex flex-col">
                  <span className="font-urban text-base font-bold tracking-tight text-foreground">
                    My Account
                  </span>
                  <span className="text-[10px] font-mono font-medium tracking-wider text-muted-foreground uppercase">
                    Personal Hub
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="flex size-8 items-center justify-center rounded-full border border-border/60 bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
                aria-label="Close account menu"
              >
                <X className="size-4" strokeWidth={2.2} />
              </button>
            </div>

            {/* Scrollable Container */}
            <div className="flex-1 overflow-y-auto py-4 space-y-4 justify-between flex flex-col">
              <div className="space-y-4">
                {/* User Profile Banner */}
                <div className="flex items-center justify-between rounded-xl border border-border/60 bg-card/60 p-3.5 shadow-2xs">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <UserAvatar
                      user={{
                        name: user.name || null,
                        image: user.image || null,
                      }}
                      className="size-10 border border-border/60 shrink-0"
                    />
                    <div className="flex flex-col min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-heading text-sm font-semibold text-foreground truncate">
                          {user.name || "User"}
                        </span>
                        {user.role === "ADMIN" && (
                          <span className="rounded-full bg-violet-500/20 border border-violet-500/30 px-1.5 py-0.5 text-[8px] font-extrabold text-violet-400 uppercase tracking-wider">
                            Admin
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] text-muted-foreground truncate font-mono">
                        {user.email || ""}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      if (onSignOut) {
                        onSignOut();
                      } else {
                        signOut({ callbackUrl: "/" });
                      }
                    }}
                    className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-border/50 bg-muted/60 text-muted-foreground hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 transition-colors cursor-pointer"
                    title="Log out"
                    aria-label="Log out"
                  >
                    <LogOut className="size-4" />
                  </button>
                </div>

                {/* Quick Access: Balance & Deposit */}
                <DrawerBalanceCard />

                {/* Quick Access: Cart */}
                <DrawerCartCard onClose={onClose} />

                {/* Account Navigation */}
                <div className="space-y-1 pt-1">
                  <p className="px-1 text-[10px] font-mono font-semibold uppercase tracking-wider text-muted-foreground">
                    Manage
                  </p>
                  <ul className="flex flex-col gap-2 p-0 m-0 list-none" role="list">
                    {user.role === "ADMIN" && (
                      <li>
                        <Link
                          href="/admin"
                          onClick={onClose}
                          className={cn(
                            "flex items-center justify-between rounded-xl p-2.5 border transition-all duration-150 group cursor-pointer",
                            segment === "admin"
                              ? "bg-primary/10 border-primary/30 text-primary shadow-2xs"
                              : "bg-card/40 border-border/40 hover:bg-muted/70 hover:border-border text-foreground"
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex size-8 items-center justify-center rounded-lg border border-violet-500/30 bg-violet-500/10 text-violet-400 shrink-0">
                              <ShieldCheck className="size-4" />
                            </div>
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                <span className="font-heading text-sm font-semibold tracking-tight text-foreground group-hover:text-primary">
                                  Admin Panel
                                </span>
                                <span className="rounded-full bg-violet-500/20 text-violet-400 border border-violet-500/30 px-1.5 py-0.5 text-[8px] font-extrabold uppercase">
                                  Admin
                                </span>
                              </div>
                              <span className="text-[11px] text-muted-foreground">
                                Manage products, keys & users
                              </span>
                            </div>
                          </div>
                          <ChevronRight className="size-4 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5 shrink-0" />
                        </Link>
                      </li>
                    )}

                    <li>
                      <Link
                        href="/dashboard/orders"
                        onClick={onClose}
                        className={cn(
                          "flex items-center justify-between rounded-xl p-2.5 border transition-all duration-150 group cursor-pointer",
                          segment === "dashboard" || segment === "orders"
                            ? "bg-primary/10 border-primary/30 text-primary shadow-2xs"
                            : "bg-card/40 border-border/40 hover:bg-muted/70 hover:border-border text-foreground"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex size-8 items-center justify-center rounded-lg border border-primary/30 bg-primary/10 text-primary shrink-0">
                            <Package className="size-4" />
                          </div>
                          <div className="flex flex-col">
                            <span className="font-heading text-sm font-semibold tracking-tight text-foreground group-hover:text-primary">
                              My Orders
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              View purchased keys & orders
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="size-4 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5 shrink-0" />
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Support Footer */}
              <div className="pt-3 border-t border-border/50 flex flex-col gap-2 mt-auto">
                {siteConfig.links.discord && (
                  <a
                    href={siteConfig.links.discord}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 rounded-xl border border-border/60 bg-muted/40 py-2.5 px-3 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  >
                    <Icons.discord className="size-4" />
                    <span>Need Help? Join Discord</span>
                  </a>
                )}
              </div>
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );

  return typeof document !== "undefined" && document.body
    ? createPortal(content, document.body)
    : null;
}
